import { LineItem, StatisticValue, UserStatisticsResponse, ValueItem } from '../api/types/userStatistics';

const count = (value: StatisticValue) => {
    const number = Number(value);
    return Number.isFinite(number) && number > 0 ? number : 0;
};

export const allocateSignupCounts = (weights: number[], total: number): number[] => {
    const sum = weights.reduce((value, weight) => value + weight, 0);
    if (!sum) return weights.map(() => 0);
    const exact = weights.map((weight) => (weight / sum) * total);
    const rounded = exact.map(Math.floor);
    const remainder = total - rounded.reduce((value, amount) => value + amount, 0);
    const order = exact
        .map((value, index) => ({ index, fraction: value - rounded[index] }))
        .sort((a, b) => b.fraction - a.fraction || a.index - b.index);
    order.slice(0, remainder).forEach(({ index }) => rounded[index]++);
    return rounded;
};

const reconcileGroup = (items: LineItem[], daily: LineItem['points'], total: number) => {
    const categories = items.map((item) => ({ ...item, points: [] as NonNullable<LineItem['points']>, value: 0 }));
    let untrackedIndex = items.findIndex((item) => item.key === 'untracked');
    const allocate = (weights: number[], budget: number) => {
        if (budget > 0 && !weights.some((weight) => weight > 0)) {
            if (untrackedIndex < 0) {
                untrackedIndex = categories.length;
                categories.push({ key: 'untracked', label: 'Untracked', points: [], value: 0 });
            }
            weights[untrackedIndex] = 1;
        }
        return allocateSignupCounts(
            categories.map((_, index) => weights[index] || 0),
            budget,
        );
    };
    if (daily) {
        const byDate = items.map((item) => {
            const counts = new Map<string | undefined, number>();
            item.points?.forEach((point) => counts.set(point.date, (counts.get(point.date) || 0) + count(point.value)));
            return counts;
        });
        for (const point of daily) {
            const counts = allocate(
                byDate.map((values) => values.get(point.date) || 0),
                count(point.value),
            );
            categories.forEach((item, index) => {
                const value = counts[index] || 0;
                item.points.push({ date: point.date, value });
                item.value += value;
            });
        }
    } else {
        const counts = allocate(
            items.map(
                (item) => count(item.value) || (item.points || []).reduce((sum, point) => sum + count(point.value), 0),
            ),
            total,
        );
        categories.forEach((item, index) => {
            item.value = counts[index] || 0;
        });
    }
    if (daily) {
        categories.forEach((item) => {
            const byDate = new Map(item.points.map((point) => [point.date, point.value]));
            item.points = daily.map((point) => ({ date: point.date, value: byDate.get(point.date) || 0 }));
        });
    }
    return {
        line: daily ? categories : undefined,
        summary: categories.map(({ points, ...item }) => item),
    };
};

// These are root's illustrative signup breakdowns, not changes to raw provider user counts.
export const reconcileRootSignupBreakdowns = (data: UserStatisticsResponse): UserStatisticsResponse => {
    const total = count(
        data.metrics?.usersSignedUp ?? data.charts?.summary?.find((item) => item.key === 'users_signed_up')?.value,
    );
    const points = data.charts?.line?.summary?.find((item) => item.key === 'users_signed_up')?.points;
    const validDaily =
        points?.length &&
        points.every(
            (point) =>
                point.date &&
                point.value != null &&
                point.value !== '' &&
                Number.isFinite(Number(point.value)) &&
                Number(point.value) >= 0,
        ) &&
        points.reduce((sum, point) => sum + Number(point.value), 0) === total
            ? points
            : undefined;
    const tracker = reconcileGroup(
        data.charts?.line?.signupsByTrackerPlatform ??
            data.charts?.signupsByTrackerPlatform ??
            data.breakdowns?.signupsByTrackerPlatform?.map((item) => ({
                key: item.platform,
                value: item.usersSignedUp,
            })) ??
            [],
        validDaily,
        total,
    );
    const provider = reconcileGroup(
        data.charts?.line?.totalUsersByProvider ??
            data.charts?.totalUsersByProvider ??
            data.breakdowns?.totalUsersByProvider?.map((item) => ({ key: item.provider, value: item.totalUsers })) ??
            [],
        validDaily,
        total,
    );
    const updateSummary = (existing: ValueItem[] | undefined, items: ValueItem[]) =>
        items.map((item) => ({ ...existing?.find((entry) => entry.key === item.key), ...item }));
    return {
        ...data,
        charts: {
            ...data.charts,
            signupsByTrackerPlatform: updateSummary(data.charts?.signupsByTrackerPlatform, tracker.summary),
            totalUsersByProvider: updateSummary(data.charts?.totalUsersByProvider, provider.summary),
            ...(data.charts?.line
                ? {
                      line: {
                          ...data.charts.line,
                          ...(tracker.line ? { signupsByTrackerPlatform: tracker.line } : {}),
                          ...(provider.line ? { totalUsersByProvider: provider.line } : {}),
                      },
                  }
                : {}),
            ...(data.charts?.pie
                ? {
                      pie: {
                          ...data.charts.pie,
                          signupsByTrackerPlatform: updateSummary(
                              data.charts.pie.signupsByTrackerPlatform,
                              tracker.summary,
                          ),
                          totalUsersByProvider: updateSummary(data.charts.pie.totalUsersByProvider, provider.summary),
                      },
                  }
                : {}),
        },
        breakdowns: {
            ...data.breakdowns,
            signupsByTrackerPlatform: tracker.summary.map((item) => ({
                ...data.breakdowns?.signupsByTrackerPlatform?.find((entry) => entry.platform === item.key),
                platform: item.key,
                usersSignedUp: item.value,
            })),
            totalUsersByProvider: [
                ...provider.summary.map((item) => ({
                    ...data.breakdowns?.totalUsersByProvider?.find((entry) => entry.provider === item.key),
                    provider: item.key,
                    totalUsers: item.value,
                })),
                ...(data.breakdowns?.totalUsersByProvider || [])
                    .filter((entry) => !provider.summary.some((item) => item.key === entry.provider))
                    .map((entry) => ({ ...entry, totalUsers: 0 })),
            ],
        },
    };
};
